import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import {
  FormAppearanceSettings,
  FormBusinessRule,
  FormConsolidationMapping,
  FormConsolidationSource,
  FormViewSettings,
} from 'src/shared/store/projects/types';

export type ConsolidationSourceForm = {
  id: number;
  alias: string;
  connection: string;
  formName: string;
  formDescription: string;
  appearanceSettings?: FormAppearanceSettings;
  viewSettings?: FormViewSettings;
  formStructure?: FieldType[];
};

export type ConsolidationSourceCleanupUpdate = {
  id: number;
  formName: string;
  formDescription: string;
  appearanceSettings?: FormAppearanceSettings;
  viewSettings?: FormViewSettings;
};

type BuildCurrentConsolidationUpdatePlanArgs = {
  currentFormConnection: string;
  currentBusinessRules: FormBusinessRule[];
  currentViewSettings?: FormViewSettings;
  rules: FormBusinessRule[];
  sourceForms: ConsolidationSourceForm[];
};

type ConsolidationUpdatePlan = {
  currentBusinessRules: FormBusinessRule[];
  currentViewSettings: FormViewSettings;
  legacyCleanupUpdates: ConsolidationSourceCleanupUpdate[];
};

const normalizeValue = (value?: string): string => value?.trim().toLowerCase() || '';

const isConsolidationRule = (rule: FormBusinessRule): boolean =>
  normalizeValue(rule.type) === 'consolidation';

const getSourceFormDisplayName = (sourceForm: ConsolidationSourceForm): string =>
  sourceForm.alias?.trim() || sourceForm.formName?.trim() || sourceForm.connection?.trim();

const resolveSourceForm = (
  rawSourceForm: string,
  sourceForms: ConsolidationSourceForm[],
): ConsolidationSourceForm | undefined => {
  const normalizedSourceForm = normalizeValue(rawSourceForm);
  if (!normalizedSourceForm) {
    return undefined;
  }

  return sourceForms.find(sourceForm =>
    [sourceForm.alias, sourceForm.formName, sourceForm.connection].some(
      candidate => normalizeValue(candidate) === normalizedSourceForm,
    ),
  );
};

const toConsolidationMappings = (raw: unknown): FormConsolidationMapping[] => {
  if (Array.isArray(raw)) {
    return raw
      .map(item => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
          return null;
        }

        const source = String(item.source || '').trim();
        const target = String(item.target || '').trim();
        if (!source || !target) {
          return null;
        }

        return { source, target };
      })
      .filter(Boolean) as FormConsolidationMapping[];
  }

  if (!raw || typeof raw !== 'object') {
    return [];
  }

  return Object.entries(raw).reduce<FormConsolidationMapping[]>((acc, [source, target]) => {
    const normalizedSource = source.trim();
    const normalizedTarget = String(target || '').trim();
    if (!normalizedSource || !normalizedTarget) {
      return acc;
    }

    acc.push({
      source: normalizedSource,
      target: normalizedTarget,
    });
    return acc;
  }, []);
};

const toLegacyMap = (mappings?: FormConsolidationMapping[]): Record<string, string> | undefined => {
  const result =
    mappings?.reduce<Record<string, string>>((acc, mapping) => {
      const source = mapping.source?.trim();
      const target = mapping.target?.trim();
      if (!source || !target) {
        return acc;
      }

      acc[source] = target;
      return acc;
    }, {}) || {};

  return Object.keys(result).length ? result : undefined;
};

export const buildConsolidationRulesFromCurrentSettings = (
  currentViewSettings: FormViewSettings | undefined,
  sourceForms: ConsolidationSourceForm[],
): FormBusinessRule[] => {
  const currentSources = currentViewSettings?.consolidation?.sources || [];

  return currentSources.reduce<FormBusinessRule[]>((acc, source, index) => {
    const resolvedSourceForm = resolveSourceForm(source.sourceForm, sourceForms);
    const displayName = resolvedSourceForm
      ? getSourceFormDisplayName(resolvedSourceForm)
      : source.sourceForm;

    acc.push({
      id: `consolidation_${normalizeValue(source.sourceForm) || index + 1}`,
      name: displayName ? `Консолидация из ${displayName}` : `consolidation_${index + 1}`,
      type: 'consolidation',
      priority: index + 1,
      enabled: true,
      config: {
        targetTable: displayName,
        operation: source.operation || 'sum',
        keyMap: toLegacyMap(source.keyMap),
        valueMap: toLegacyMap(source.valueMap),
      },
    });

    return acc;
  }, []);
};

const buildCurrentSourcePayload = (
  rule: FormBusinessRule,
  sourceForms: ConsolidationSourceForm[],
): FormConsolidationSource => {
  const sourceTable = String(rule.config?.targetTable || '').trim();
  const resolvedSourceForm = resolveSourceForm(sourceTable, sourceForms);

  if (!resolvedSourceForm) {
    throw new Error(`Не найдена таблица-источник для консолидации: ${sourceTable}`);
  }

  return {
    sourceForm: resolvedSourceForm.connection.trim(),
    operation: String(rule.config?.operation || 'sum')
      .trim()
      .toLowerCase() as FormConsolidationSource['operation'],
    keyMap: toConsolidationMappings(rule.config?.keyMap),
    valueMap: toConsolidationMappings(rule.config?.valueMap),
  };
};

const buildLegacyCleanupUpdates = (
  currentFormConnection: string,
  sourceForms: ConsolidationSourceForm[],
): ConsolidationSourceCleanupUpdate[] => {
  const normalizedCurrentConnection = normalizeValue(currentFormConnection);
  if (!normalizedCurrentConnection) {
    return [];
  }

  return sourceForms.reduce<ConsolidationSourceCleanupUpdate[]>((acc, sourceForm) => {
    const existingSources = sourceForm.viewSettings?.consolidation?.sources || [];
    const filteredSources = existingSources.filter(
      source => normalizeValue(source.sourceForm) !== normalizedCurrentConnection,
    );

    if (filteredSources.length === existingSources.length) {
      return acc;
    }

    acc.push({
      id: sourceForm.id,
      formName: sourceForm.alias || sourceForm.formName,
      formDescription: sourceForm.formDescription,
      appearanceSettings: sourceForm.appearanceSettings,
      viewSettings: {
        ...(sourceForm.viewSettings || {}),
        consolidation: filteredSources.length ? { sources: filteredSources } : undefined,
      },
    });

    return acc;
  }, []);
};

export const buildCurrentConsolidationUpdatePlan = ({
  currentFormConnection,
  currentBusinessRules,
  currentViewSettings,
  rules,
  sourceForms,
}: BuildCurrentConsolidationUpdatePlanArgs): ConsolidationUpdatePlan => {
  const normalizedCurrentConnection = currentFormConnection.trim();
  if (!normalizedCurrentConnection) {
    throw new Error('Не удалось определить текущую форму для консолидации');
  }

  const nextRules = rules.filter(isConsolidationRule);
  const currentSources = nextRules.map(rule => buildCurrentSourcePayload(rule, sourceForms));

  return {
    currentBusinessRules: currentBusinessRules.filter(rule => !isConsolidationRule(rule)),
    currentViewSettings: {
      ...(currentViewSettings || {}),
      consolidation: currentSources.length ? { sources: currentSources } : undefined,
    },
    legacyCleanupUpdates: buildLegacyCleanupUpdates(normalizedCurrentConnection, sourceForms),
  };
};
